import type { CountryCode, Option, QliroCreateOrderRequest, QliroOrder } from 'ecosystem'
import { qliroApi } from 'api'
import {
  convertMinorUnitToValue,
  DEFAULT_COUNTRY,
  DEFAULT_CURRENCY,
  getFullLocale,
  IS_PRODUCTION,
  resolveUrlPath
} from 'shared-utils'
import { qliroOrderLineFactory } from 'api/utils/qliro/checkout'
import { QLIRO_API_KEY } from 'api/utils/qliro/constants'
import { runCreateOrder } from './runCreateOrder'
import {
  type BuildPayloadArgs,
  type CreateOrderContext,
  type CreateOrderHandlerOpts,
  type CreateOrderRouteResponse,
  type PaymentProviderAdapter
} from './types'

const buildQliroPayload = ({
  ctx,
  opts,
  backendOrder,
  sessionCountry,
  configCountry,
  shippingInfo
}: BuildPayloadArgs<CountryCode>): QliroCreateOrderRequest => {
  const qliroOptions = opts.paymentProviderConfig?.providerOptions?.qliro
  const { items, cartDiscountCode, cartStateUuid } = ctx.payload
  const { orderLines } = qliroOrderLineFactory(
    items,
    cartDiscountCode,
    configCountry,
    opts.buyerVat
  )
  // Qliro push URLs must be HTTPS on port 443, use a mock domain in dev
  const pushDomain = IS_PRODUCTION ? opts.redirectDomain : 'https://granditude.com'

  const payload = {
    MerchantApiKey: QLIRO_API_KEY,
    MerchantReference: (backendOrder.orderNumber ?? '').substring(0, 25),
    Currency: qliroOptions?.purchaseCurrency || DEFAULT_CURRENCY,
    Country: sessionCountry || DEFAULT_COUNTRY,
    Language: getFullLocale(ctx.locale, { no: 'nb-NO', en: 'en-US' }).toLowerCase(),
    OrderItems: orderLines,
    MerchantConfirmationUrl: resolveUrlPath(
      opts.redirectDomain,
      `/confirmation?order_id=${backendOrder.orderNumber}`
    ),
    MerchantCheckoutStatusPushUrl: resolveUrlPath(
      pushDomain,
      `/api/orders/push?order_id=${backendOrder.orderNumber}`
    ),
    MerchantOrderManagementStatusPushUrl: resolveUrlPath(
      pushDomain,
      `/api/orders/push?order_id=${backendOrder.orderNumber}`
    ),
    MerchantOrderValidationUrl: resolveUrlPath(
      pushDomain,
      `/api/orders/validate-order?order_id=${backendOrder.orderNumber}`
    ),
    MerchantTermsUrl: resolveUrlPath(opts.redirectDomain, qliroOptions?.termsUrl ?? '/terms'),

    ...(qliroOptions?.integrityPolicyUrl
      ? {
          MerchantIntegrityPolicyUrl: resolveUrlPath(
            opts.redirectDomain,
            qliroOptions.integrityPolicyUrl
          )
        }
      : {}),

    MerchantProvidedMetadata: [{ Key: 'cartStateUuid', Value: cartStateUuid ?? '' }],
    //styling
    PrimaryColor: qliroOptions?.primaryColor ?? opts.color,
    CallToActionColor: qliroOptions?.callToActionColor ?? opts.color,
    CallToActionHoverColor: qliroOptions?.callToActionHoverColor,
    BackgroundColor: qliroOptions?.backgroundColor,
    CornerRadius: qliroOptions?.cornerRadius,
    ButtonCornerRadius: qliroOptions?.buttonCornerRadius
  } satisfies QliroCreateOrderRequest

  if (!shippingInfo) {
    return payload
  }

  const priceIncVat = convertMinorUnitToValue(shippingInfo.price)
  const priceExVat = convertMinorUnitToValue(shippingInfo.price - shippingInfo.tax_amount)

  // Qliro adds the selected `AvailableShippingMethods` entry on top of
  // `OrderItems`. Fine for the legacy single flat rate, but a store with
  // configured delivery options has an FE delivery step that pushes its own
  // `Shipping` item on every change — leaving a shipping method in place would
  // charge freight twice. There it goes straight into the items instead.
  return opts.shippingProviderConfig?.defaultOptions?.methods?.length
    ? {
        ...payload,
        OrderItems: [
          ...payload.OrderItems,
          {
            MerchantReference: shippingInfo.id,
            Description: shippingInfo.name,
            Type: 'Shipping',
            Quantity: 1,
            PricePerItemIncVat: priceIncVat,
            PricePerItemExVat: priceExVat,
            VatRate: convertMinorUnitToValue(shippingInfo.tax_rate)
          }
        ]
      }
    : {
        ...payload,
        AvailableShippingMethods: [
          {
            MerchantReference: shippingInfo.id,
            DisplayName: shippingInfo.name,
            PriceIncVat: priceIncVat,
            PriceExVat: priceExVat
          }
        ]
      }
}

/**
 * Qliro's `createOrder` answers with an OrderId only — the HTML snippet the
 * checkout renders comes from a follow-up `retrieveOrder`. Both hops live here
 * so the pipeline sees the single `{ error, data }` every other provider gives
 * it, instead of the four duplicated two-step dances the branches used to hold.
 */
const createQliroSession = async (
  payload: QliroCreateOrderRequest
): Promise<{ error?: Option<string>; data?: Option<QliroOrder> }> => {
  const { error, data } = await qliroApi.checkout.createOrder(payload)

  if (error || !data) {
    return { error: error || 'Unknown error' }
  }

  return qliroApi.checkout.retrieveOrder(data.OrderId)
}

const qliroAdapter: PaymentProviderAdapter<QliroOrder, QliroCreateOrderRequest, CountryCode> = {
  name: 'QLIRO',
  configCountry: (opts) => opts.paymentProviderConfig?.providerOptions?.qliro?.countryCode,
  availableCountries: (opts) =>
    opts.paymentProviderConfig?.providerOptions?.qliro?.availableCountries,
  orderLineFactory: qliroOrderLineFactory,
  buildPayload: buildQliroPayload,
  createSession: createQliroSession,
  respond: (qliroResponse) => ({ qliroResponse })
}

export const qliroPaymentProvider = (
  ctx: CreateOrderContext,
  opts: CreateOrderHandlerOpts
): Promise<CreateOrderRouteResponse> => runCreateOrder(ctx, opts, qliroAdapter)
